import { datadogRum } from '@datadog/browser-rum';
import { vuePlugin } from '@datadog/browser-rum-vue';
import { isProductionEnvironment, parseHostname } from '@sera/core';

/**
 * Datadog RUM (Real User Monitoring) initialization for the Customer Portal.
 *
 * Mirrors the admin-web RUM setup (api/app/javascript/lib/datadog.js) but adapts
 * it to this workspace's runtime-env-resolution model: there is a single build
 * artifact and the environment is derived from `window.location.hostname` at
 * runtime (see libs/core/src/env/hostname.ts), NOT baked in per-build. That is
 * why the `env` RUM dimension is computed here rather than read from VITE_ENV
 * (which is unset in deployed builds — a separate, pre-existing gap that also
 * affects Sentry's env tag; tracked as a follow-up on the RUM ticket).
 *
 * Gating mirrors initSentry: this is a no-op until the RUM application is
 * provisioned in Datadog and its (public, client-side) credentials are supplied
 * via the VITE_DD_RUM_* env vars, and it never runs in local dev.
 *
 * User identity + tenant context is attached separately, in libs/core's
 * `datadog/context.ts` (wired into the auth store next to identifySentry), for
 * the same dependency-direction reason documented there: the auth store is a
 * leaf in libs/core and cannot import this app-layer plugin.
 *
 * Vue integration uses the official `@datadog/browser-rum-vue` plugin:
 * - `vuePlugin({ router: true })` here, PLUS the wrapped `createRouter` from
 *   `@datadog/browser-rum-vue/vue-router-v4` in src/router — both are required
 *   together. `router: true` flips RUM to manual view tracking, so views come
 *   exclusively from the router's afterEach hook (with normalized names for
 *   dynamic routes, e.g. `/invoices/:id` instead of `/invoices/123`).
 * - `addVueError` is installed as the Vue `errorHandler` in main.ts.
 */

/**
 * Resolve the Datadog `env` dimension from the current hostname.
 * - production hostname (no env segment) → 'prod'
 * - env-segment hostname (staging/blue/green/…) → that segment
 * - anything else → 'unknown'
 * Only ever called from a deployed build (init bails in dev before this runs).
 */
export function resolveRumEnv(): string {
  if (isProductionEnvironment()) return 'prod';
  const { envSegment, hasEnvSegment } = parseHostname(window.location.hostname);
  return hasEnvSegment ? envSegment : 'unknown';
}

/**
 * Loopback host detection — true when the app is served from localhost, i.e.
 * the E2E preview server (`nx run customer-portal:preview` on :4300) or any
 * local `vite preview`. A deployed Sera host is always `tenant[.env].schedule
 * .online`, never a loopback address. This is the reliable "not a real deploy"
 * signal: `import.meta.env.DEV` is FALSE in a preview build, and on localhost
 * `isProductionEnvironment()` returns TRUE (no env segment ⇒ prod), so without
 * this check RUM would boot in E2E and report as env:'prod'.
 */
function isLoopbackHost(): boolean {
  const h = window.location.hostname;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '::1' ||
    h === '[::1]' ||
    h.endsWith('.localhost')
  );
}

export function initDatadogRum(): void {
  // The RUM application ID + client token are public, client-side identifiers
  // (they ship in the browser bundle, exactly like the Sentry DSN). They are
  // provisioned per the RUM ticket via GitLab CI project variables, which Vite
  // inlines at build time.
  const applicationId = import.meta.env.VITE_DD_RUM_APPLICATION_ID;
  const clientToken = import.meta.env.VITE_DD_RUM_CLIENT_TOKEN;
  const isDev = import.meta.env.DEV;
  // Automated browsers (Playwright/E2E). The cross-browser E2E job runs a
  // PRODUCTION build (isDev false) served on localhost, so isDev alone let RUM
  // boot during E2E: it initialized as env:'prod' (localhost has no env segment
  // ⇒ isProductionEnvironment() true), shipped synthetic session replays into
  // the production RUM app, and its boot-time work (replay recorder, resource/
  // long-task observers) raced the Vue mount that fires our onMounted analytics
  // — flaking the invoice_detail_viewed E2E (ENG-139 follow-up). Gate on both
  // webdriver and loopback so RUM is off for automation and any local preview.
  const isAutomated =
    typeof navigator !== 'undefined' && navigator.webdriver === true;

  // No-op until credentials are provisioned, and never in local dev, an
  // automated browser, or a loopback (preview) host — RUM is strictly for real
  // end users on a deployed host. Mirrors initSentry's `!!VITE_SENTRY_DSN &&
  // !isDev` gate and the admin-web `applicationId && clientToken &&
  // !['development','test'].includes(env)` guard.
  if (
    !applicationId ||
    !clientToken ||
    isDev ||
    isAutomated ||
    isLoopbackHost()
  )
    return;

  datadogRum.init({
    applicationId,
    clientToken,
    site: 'datadoghq.com',
    service: 'customer-portal',
    env: resolveRumEnv(),
    sessionSampleRate: 100,
    // Session Replay sampling, dialed to 10% for RUM cost control. Safe to
    // enable here because defaultPrivacyLevel: 'mask' (below) masks ALL
    // text/inputs in the replay — stricter than admin-web's 'mask-user-input' —
    // which matters for the financial/PII surfaces this portal renders. Dial
    // down or to 0 if a privacy review of the payment/quote screens calls for it.
    sessionReplaySampleRate: 10,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask',
    // Official Vue integration. Requires the wrapped createRouter in src/router
    // (see this file's header) — together they enable normalized SPA view names.
    plugins: [vuePlugin({ router: true })],
  });
}
